import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import { BookOpen, ChevronDown, ChevronUp } from 'lucide-react';

/*
 * WO-34 — "How tender retrieval works": a plain-language guide for admins, shown on
 * Admin → Tender Sources. Static text only: it calls nothing and changes nothing.
 * Closed by default; the admin's choice to keep it open is remembered in this browser.
 */

const OPEN_KEY = 'm2b.tenderGuideOpen';

function readOpen() {
  try { return window.localStorage.getItem(OPEN_KEY) === '1'; } catch (_) { return false; }
}
function saveOpen(v) {
  try { window.localStorage.setItem(OPEN_KEY, v ? '1' : '0'); } catch (_) { /* private window: just not remembered */ }
}

const P = ({ children }) => <p className="text-sm text-muted-foreground leading-relaxed mb-2">{children}</p>;
const Li = ({ children }) => <li className="text-sm text-muted-foreground leading-relaxed">{children}</li>;

const TROUBLE = [
  ['“Add-on not found in this Chrome”', 'The Mid2Bid Collector is not installed in the Chrome you are using, or Chrome turned it off.', 'Install it once (INSTALL.md), or switch it back on in chrome://extensions, then reload this page.'],
  ['“Not connected yet.”', 'The add-on is installed but has no key, so Mid2Bid would refuse what it sends.', 'Press Connect the collector on this page. You never see or copy the key.'],
  ['A server source shows “failed” or “empty”', '“Failed”: the portal refused or could not be reached. “Empty”: it answered but gave no tenders, usually because it changed. The other two server sources are not affected.', 'Try the refresh again later. If it stays that way, tell MATT which one.'],
  ['“No tender rows found on this page”', 'The portal changed the layout of its list, so the add-on cannot read it. Nothing is imported.', 'Tell MATT which portal.'],
  ['“The portal lists no open tenders here today”', 'The list is empty on the portal itself (Sharjah often is).', 'Nothing. Try another day.'],
  ['“… asked us to slow down — waiting”', 'The portal wants fewer requests per minute. The add-on waits and continues by itself.', 'Nothing. Leave the tab open.'],
  ['“Mid2Bid refused the import”', 'Mid2Bid did not accept what the add-on sent. Tenders sent before this point are saved; the rest are not.', 'Tell MATT, with the portal name and the message.'],
  ['“still being translated/classified” under a portal', 'Tenders just arrived and are being translated into English and given industries. Alerts go out after that.', 'Nothing. It clears by itself within minutes.'],
];

export default function TenderRetrievalGuide() {
  const [open, setOpen] = useState(readOpen);
  const toggle = () => { setOpen((v) => { saveOpen(!v); return !v; }); };

  return (
    <Card data-testid="tender-retrieval-guide">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-indigo-600" /> How tender retrieval works</CardTitle>
          <CardDescription>A short guide to what happens when you press a button on this page, and what to do when something looks wrong.</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={toggle} className="gap-1 shrink-0" data-testid="tender-guide-toggle" aria-expanded={open}>
          {open ? <>Hide guide <ChevronUp className="w-4 h-4" /></> : <>Show guide <ChevronDown className="w-4 h-4" /></>}
        </Button>
      </CardHeader>

      {open && (
        <CardContent data-testid="tender-guide-body">
          <Accordion type="multiple" defaultValue={['two-ways']}>

            <AccordionItem value="two-ways">
              <AccordionTrigger>1. Tenders arrive in two ways</AccordionTrigger>
              <AccordionContent>
                <P>
                  <b>The server way.</b> Three portals let the Mid2Bid server read their list directly: <b>Abu Dhabi (ADGPG)</b>,
                  <b> Bahrain Tender Board</b> and <b>Qatar Ashghal</b>. One button, <b>Refresh server sources</b>, reads all three,
                  one after the other. It takes a few minutes and you can leave the page while it runs.
                </P>
                <P>
                  <b>The collector way.</b> The other eight portals are read by the <b>Mid2Bid Collector</b>, a small Chrome add-on,
                  from your own Chrome: Etimad, Muqawil, Qatar Monaqasat, Kuwait CAPT, Oman Tender Board, Dubai eSupply,
                  Sharjah eProcurement and UAE Federal Procurement. It works like a careful assistant copying the public list
                  you have on screen into Mid2Bid.
                </P>
                <P>
                  <b>Why two ways?</b> Some portals block computers outside the Gulf (the Mid2Bid server is abroad),
                  and some ask programs not to read them automatically. Read from your own browser, in the region, they
                  show their public list like to any visitor. So the server does what it is allowed to do, and the rest is
                  done by a person, with the add-on doing the copying.
                </P>
                <P>
                  <b>Nothing runs on a timer.</b> Every collection is started by an admin. There is no fixed schedule:
                  collect when you want fresh tenders.
                </P>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="refresh-day">
              <AccordionTrigger>2. A refresh, step by step</AccordionTrigger>
              <AccordionContent>
                <ol className="list-decimal pl-5 space-y-1 mb-2">
                  <Li>On this page, press <b>Refresh server sources</b>. You do not need to wait for it.</Li>
                  <Li>In the Collector table below, click <b>Open &amp; collect</b> on a portal&apos;s row. The portal opens in a new tab with the Mid2Bid panel already open on the right.</Li>
                  <Li>
                    On Etimad, Muqawil, Monaqasat, Kuwait and Oman, press <b>Read the list</b>: the add-on turns the pages by itself,
                    a few seconds apart, and stops on its own. On Dubai, Sharjah and the UAE federal portal it reads only the page
                    on your screen, straight away: for the next page, go to it on the portal and press <b>Read the list</b>.
                  </Li>
                  <Li>Check the counts in the panel and press <b>Import</b>. The panel says how many were new and how many updated.</Li>
                  <Li>Repeat for the next portal. Tabs can be closed once the panel says <b>Imported</b>.</Li>
                </ol>
                <P>The first collection of a portal takes longest (Etimad: a few minutes). After that, only what is new is read, usually in under a minute.</P>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="what-it-keeps">
              <AccordionTrigger>3. What gets imported, and what does not</AccordionTrigger>
              <AccordionContent>
                <ul className="list-disc pl-5 space-y-1 mb-2">
                  <Li><b>Imported:</b> open tenders not yet in Mid2Bid, and tenders already in Mid2Bid whose deadline or title changed (for example a deadline extension).</Li>
                  <Li><b>Left out:</b> tenders already closed; tenders already in Mid2Bid with nothing changed; rows the portal left without a real title or without a readable closing date. Mid2Bid never guesses a title or a date.</Li>
                  <Li><b>Where it stops:</b> the portals list the newest tenders first, so the add-on stops by itself when it reaches pages that bring nothing new (already stored or already closed). On Etimad it waits for three such pages in a row, because a few long-running tenders sit among closed ones.</Li>
                  <Li><b>Links:</b> every tender links back to the official portal, never to a copy site.</Li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="after-import">
              <AccordionTrigger>4. What happens after the import</AccordionTrigger>
              <AccordionContent>
                <P>
                  The tenders appear in the Tenders list at once. In the background, within minutes, Mid2Bid then does three things,
                  in this order: <b>translates</b> Arabic titles into English (the Arabic original is kept), gives each tender its
                  <b> industries</b>, and only then checks the users&apos; <b>alerts</b> and sends the notifications and emails.
                  Industries come before alerts on purpose, so that an alert on an industry can match the new tenders.
                </P>
                <P>While this runs, the portal&apos;s row below shows “still being translated/classified”.</P>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="read-page">
              <AccordionTrigger>5. How to read this page</AccordionTrigger>
              <AccordionContent>
                <ul className="list-disc pl-5 space-y-1 mb-2">
                  <Li><b>Server sources:</b> “healthy” means the last refresh of that portal worked and found tenders; “never run” means it has not been refreshed yet; “failed” or “empty” means the last refresh did not work, with the portal&apos;s error underneath when there is one.</Li>
                  <Li><b>Open:</b> tenders from that portal in Mid2Bid whose deadline has not passed. This is what customers can still bid for.</Li>
                  <Li><b>Stored:</b> all tenders from that portal in Mid2Bid, closed ones included.</Li>
                  <Li><b>Last collected:</b> the last import through the add-on, by any admin.</Li>
                  <Li><b>Add-on line:</b> whether the add-on is installed in the Chrome you are using right now, and its version.</Li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="safety">
              <AccordionTrigger>6. What the add-on never does</AccordionTrigger>
              <AccordionContent>
                <ul className="list-disc pl-5 space-y-1 mb-2">
                  <Li>Log in to a portal or use your portal account. It reads only public lists.</Li>
                  <Li>Run by itself, in the background or at night.</Li>
                  <Li>Download tender documents.</Li>
                  <Li>Send anything anywhere except to Mid2Bid.</Li>
                  <Li>Work for a non-admin: its key stops working the moment its owner is no longer an admin. <b>Disconnect</b> on this page cancels it at once.</Li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="trouble">
              <AccordionTrigger>7. When something looks wrong</AccordionTrigger>
              <AccordionContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="tender-guide-trouble">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b">
                        <th className="py-2 pr-3">You see</th>
                        <th className="py-2 pr-3">What it means</th>
                        <th className="py-2">What to do</th>
                      </tr>
                    </thead>
                    <tbody>
                      {TROUBLE.map(([see, means, todo]) => (
                        <tr key={see} className="border-b last:border-0 align-top">
                          <td className="py-2 pr-3 font-medium">{see}</td>
                          <td className="py-2 pr-3 text-muted-foreground">{means}</td>
                          <td className="py-2 text-muted-foreground">{todo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AccordionContent>
            </AccordionItem>

          </Accordion>
        </CardContent>
      )}
    </Card>
  );
}
